// src/pages/dashboard/branch/menu-items/components/add-item-modal/tabs/DynamicOptions.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FiPlus, FiX, FiChevronDown, FiChevronUp, FiSettings, FiPackage, FiTrendingUp } from 'react-icons/fi';
import {
    BranchActionConfig,
    ItemVariant,
    ActionType,
    getActionTypes,
    getAllBranchActionsByBranchId,
    getAllVariantsByItemId,
} from '../../../../../../../utils/supabaseClient';
import ItemVariantOverrideModal from '../ItemVariantOverrideModal';


interface DynamicOptionsProps {
    itemId?: string;
    branchId: string;
    selectedVariants: ItemVariant[];
    onVariantsChange: (variants: ItemVariant[]) => void;
}

const DynamicOptions: React.FC<DynamicOptionsProps> = ({
    itemId,
    branchId,
    selectedVariants,
    onVariantsChange,
}) => {
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;

    // Helper function to get localized field value
    const getLocalizedField = (option: any, field: 'name' | 'note' | 'description'): string => {
        if (currentLanguage === 'ar' && option.ar && option.ar[field]) {
            return option.ar[field];
        }
        return option[field] || '';
    };

    // State
    const [actionTypes, setActionTypes] = useState<ActionType[]>([]);
    const [branchActions, setBranchActions] = useState<BranchActionConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
    const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false);
    const [activeOverrideConfig, setActiveOverrideConfig] = useState<BranchActionConfig | null>(null);
    const [activeVariant, setActiveVariant] = useState<ItemVariant | null>(null);
    const [isSavingOverride, setIsSavingOverride] = useState(false);
    const [overrideError, setOverrideError] = useState<string | null>(null);

    // Load data on component mount or when itemId changes
    useEffect(() => {
        loadInitialData();
    }, [branchId, itemId]);



    // Container ref for scrolling and sticky header
    const containerRef = useRef<HTMLDivElement | null>(null);

    const scrollToSection = useCallback((sectionId: string) => {
        const panel = containerRef.current?.querySelector(`[data-config-id="${sectionId}"]`);
        if (panel && panel instanceof HTMLElement) {
            panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, []);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            console.log('Loading variant data for branch:', branchId);

            if (!branchId) {
                console.error('No branch ID provided');
                setLoading(false);
                return;
            }

            // Load branch actions and action types
            const [actionTypesResult, branchActionsResult] = await Promise.all([
                getActionTypes(),
                getAllBranchActionsByBranchId(branchId),
            ]);

            console.log('Action types result:', actionTypesResult);
            console.log('Branch actions result:', branchActionsResult);

            if (actionTypesResult.data) {
                setActionTypes(actionTypesResult.data);
            }

            if (branchActionsResult.data) {
                setBranchActions(branchActionsResult.data);
            }

            // Load existing variants if editing an existing item
            if (itemId && selectedVariants.length === 0) {
                console.log('Loading existing variants for item:', itemId);
                const variantsResult = await getAllVariantsByItemId(itemId);
                console.log('Existing variants result:', variantsResult);

                if (variantsResult.data && variantsResult.data.length > 0) {
                    // Transform the API response to match our expected format
                    const transformedVariants: ItemVariant[] = variantsResult.data.map((variant: any) => ({
                        id: variant.id,
                        data: variant.data,
                        item_id: variant.item_id,
                        form_name: variant.form_name,
                        action_type: variant.action_type,
                        branch_action_config_id: variant.branch_action_config_id,
                        override_data: variant.override_data || null,
                    }));

                    console.log('Transformed variants:', transformedVariants);
                    onVariantsChange(transformedVariants);
                }
            }
        } catch (error) {
            console.error('Error loading variant data:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleSection = (sectionId: string) => {
        const newExpanded = new Set(expandedSections);
        if (newExpanded.has(sectionId)) {
            newExpanded.delete(sectionId);
        } else {
            newExpanded.add(sectionId);
        }
        setExpandedSections(newExpanded);
    };

    const addVariant = (branchActionConfigId: string) => {
        // Check if variant already exists
        const existingVariant = selectedVariants.find(v => v.branch_action_config_id === branchActionConfigId);
        if (existingVariant) {
            console.warn('Variant already exists for this configuration:', branchActionConfigId);
            return;
        }

        // Always use local state - variants will be persisted when form submits
        const newVariant: ItemVariant = {
            id: `temp-${Date.now()}`, // Temporary ID until form submits
            data: null,
            item_id: itemId || '', // Will be set properly when item is saved
            form_name: '',
            action_type: '',
            branch_action_config_id: branchActionConfigId,
            override_data: null,
        };
        onVariantsChange([...selectedVariants, newVariant]);
    };

    const openOverrideModal = (config: BranchActionConfig, variant: ItemVariant) => {
        // Allow override modal for both real and temporary variants
        setOverrideError(null);
        setActiveOverrideConfig(config);
        setActiveVariant(variant);
        setIsOverrideModalOpen(true);
    };

    const closeOverrideModal = () => {
        setIsOverrideModalOpen(false);
        setActiveOverrideConfig(null);
        setActiveVariant(null);
        setIsSavingOverride(false);
        setOverrideError(null);
    };

    const handleSaveOverride = async (overrideData: any | null) => {
        if (!activeVariant) {
            return;
        }

        setIsSavingOverride(true);
        setOverrideError(null);
        
        // Always update local state - changes will be persisted when form submits
        const updatedVariant = {
            ...activeVariant,
            override_data: overrideData,
        };

        const nextVariants = selectedVariants.map((variant) =>
            variant.id === activeVariant.id ? updatedVariant : variant
        );
        onVariantsChange(nextVariants);
        closeOverrideModal();
    };

    const removeVariant = (variantId: string) => {
        // Always update local state - deletion will be handled when form submits
        onVariantsChange(selectedVariants.filter(v => v.id !== variantId));
    };

    const getVariantIcon = (actionType: string) => {
        switch (actionType.toLowerCase()) {
            case 'selection':
                return <FiSettings className="w-4 h-4" />;
            case 'upgrade':
                return <FiTrendingUp className="w-4 h-4" />;
            default:
                return <FiPackage className="w-4 h-4" />;
        }
    };

    const getVariantColorClasses = (actionType: string) => {
        switch (actionType.toLowerCase()) {
            case 'selection':
                return {
                    border: 'border-blue-400 dark:border-blue-500',
                    bg: 'bg-blue-50 dark:bg-blue-900/30',
                    icon: 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400',
                    badge: 'px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
                    button: ' hover:bg-blue-600',
                    ring: 'ring-blue-300 dark:ring-blue-500'
                };
            case 'upgrade':
                return {
                    border: 'border-purple-400 dark:border-purple-500',
                    bg: 'bg-purple-50 dark:bg-purple-900/30',
                    icon: 'bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400',
                    badge: 'px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300',
                    button: 'bg-purple-500 hover:bg-purple-600',
                    ring: 'ring-purple-300 dark:ring-purple-500'
                };
            default:
                return {
                    border: 'border-orange-400 dark:border-orange-500',
                    bg: 'bg-orange-50 dark:bg-orange-900/30',
                    icon: 'bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400',
                    badge: 'px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300',
                    button: 'bg-orange-500 hover:bg-orange-600',
                    ring: 'ring-orange-300 dark:ring-orange-500'
                };
        }
    };

    // Shared button classes to unify the "add" and "edit/customize" styling
    const baseActionClass = 'w-full px-4 py-2 text-white rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed';
    const removeActionClass = 'w-full px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors duration-200 flex items-center justify-center gap-2';

    const renderSelectionVariant = (config: BranchActionConfig, colorClasses: any) => {
        const selectedVariant = selectedVariants.find(v => v.branch_action_config_id === config.id);
        const isSelected = !!selectedVariant;

        // For existing items with loaded variants, use the variant's data property
        // For new items or when no variant data is loaded, fall back to config.data
        const variantData = selectedVariant?.data;
        const baseOptions = Array.isArray(variantData) ? variantData : Array.isArray(config.data) ? config.data : [];

        // Override data source: items_variants.override_data (when item needs custom options)
        const overrideOptions = Array.isArray(selectedVariant?.override_data)
            ? (selectedVariant?.override_data as any[])
            : null;
        const overrideActive = selectedVariant?.override_data !== null && selectedVariant?.override_data !== undefined;

        // Render priority: override_data > variant.data > config.data
        const optionsToRender = overrideActive && Array.isArray(overrideOptions)
            ? overrideOptions
            : baseOptions;

        const hasOverride = overrideActive;

        if ((!optionsToRender || optionsToRender.length === 0)) {
            return (
                <div className="space-y-3 ">
                    <div className="text-sm text-gray-500 italic p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        {t('common.variants.noOptionsConfigured')}
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        {isSelected ? (
                            <button
                                type="button"
                                onClick={() => removeVariant(selectedVariant.id)}
                                className={removeActionClass}
                            >
                                <FiX className="w-4 h-4" />
                                {t('common.variants.removeFromItem')}
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => addVariant(config.id)}
                                className={`${baseActionClass} ${colorClasses.button}`}
                            >
                                <FiPlus className="w-4 h-4" />
                                {t('common.variants.addToItem')}
                            </button>
                        )}
                    </div>
                </div>
            );
        }

        return (
            <div className="space-y-3  ">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {optionsToRender.map((option: any, index: number) => {
                        const optionData = option as {
                            name?: string;
                            note?: string;
                            description?: string;
                            price?: number;
                            is_active?: boolean;
                            is_default?: boolean;
                        };
                        const isActive = optionData.is_active !== false;

                        return (
                            <div key={`${optionData.name || 'option'}-${index}`}
                                className={`p-3 border-2 rounded-lg transition-all duration-200 ${isActive
                                    ? 'border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700'
                                    : 'border-gray-200 bg-gray-100 dark:bg-gray-800 dark:border-gray-600'
                                    }`}
                            >
                                <div className="flex justify-between items-start mb-2">                                  <h5 className="font-semibold text-gray-900 dark:text-white">
                                    {getLocalizedField(option, 'name')}
                                </h5>
                                    {optionData.is_default && (
                                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                            {t('common.variants.default')}
                                        </span>
                                    )}
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                                    <div className="flex justify-between">
                                        <span>{t('common.variants.price')}:</span>
                                        <span className="font-medium">${optionData.price ?? 0}</span>
                                    </div>
                                    {(optionData.note || (option.ar && option.ar.note)) && (
                                        <div className="flex justify-between">
                                            <span>{t('common.variants.note')}:</span>
                                            <span className="font-medium">{getLocalizedField(option, 'note')}</span>
                                        </div>
                                    )}
                                    {(optionData.description || (option.ar && option.ar.description)) && (
                                        <div className="flex justify-between">
                                            <span>{t('common.variants.description', 'Description')}:</span>
                                            <span className="font-medium">{getLocalizedField(option, 'description')}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between">
                                        <span>{t('common.variants.status')}:</span>
                                        <span className={`font-medium ${isActive ? 'text-green-600' : 'text-red-600'}`}>
                                            {isActive ? t('common.variants.active') : t('common.variants.inactive')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    {isSelected ? (
                        <div className="flex flex-col sm:flex-row gap-2">
                            <button
                                type="button"
                                onClick={() => selectedVariant && openOverrideModal(config, selectedVariant)}
                                disabled={!selectedVariant}
                                className={`${baseActionClass} bg-amber-500 hover:bg-amber-600`}
                            >
                                <FiSettings className="w-4 h-4" />
                                {t('common.variants.customizeOptions', 'Customize Options')}
                                {hasOverride && (
                                    <span className="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded-full">
                                        {t('common.variants.overrideActive', 'Override active')}
                                    </span>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => removeVariant(selectedVariant.id)}
                                className={removeActionClass}
                            >
                                <FiX className="w-4 h-4" />
                                {t('common.variants.removeFromItem')}
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => addVariant(config.id)}
                            disabled={isSelected}
                            className={`${baseActionClass} ${colorClasses.button}`}
                        >
                            <FiPlus className="w-4 h-4" />
                            {isSelected ? t('common.variants.alreadyAdded', 'Already Added') : t('common.variants.addToItem')}
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const renderUpgradeVariant = (config: BranchActionConfig, colorClasses: any) => {
        const selectedVariant = selectedVariants.find(v => v.branch_action_config_id === config.id);
        const isSelected = !!selectedVariant;

        // Check if this is combo data structure or options array
        // Consider both variant data (for existing items) and config data (for new items)
        const upgradeVariantData = selectedVariant?.data;
        const upgradeConfigData = config.data;

        const isComboData = (
            (upgradeVariantData && typeof upgradeVariantData === 'object' && !Array.isArray(upgradeVariantData) && 'combo_price' in upgradeVariantData) ||
            (upgradeConfigData && typeof upgradeConfigData === 'object' && !Array.isArray(upgradeConfigData) && 'combo_price' in upgradeConfigData)
        );

        // Handle combo data structure
        if (isComboData) {
            // For existing items with loaded variants, use the variant's data property
            // For new items, fall back to config.data
            const variantComboData = upgradeVariantData && typeof upgradeVariantData === 'object' && !Array.isArray(upgradeVariantData) && 'combo_price' in upgradeVariantData
                ? upgradeVariantData as { combo_price?: number; items?: any[] }
                : null;

            const baseComboData = variantComboData || (upgradeConfigData as { combo_price?: number; items?: any[] });
            const baseItems = baseComboData.items || [];

            // Override data source: items_variants.override_data (when item needs custom combo)
            const overrideComboData = selectedVariant?.override_data && typeof selectedVariant.override_data === 'object' && !Array.isArray(selectedVariant.override_data)
                ? selectedVariant.override_data as { combo_price?: number; items?: any[] }
                : null;
            const overrideActive = selectedVariant?.override_data !== null && selectedVariant?.override_data !== undefined;

            // Render priority: override_data > variant.data > config.data
            const comboToRender = overrideActive && overrideComboData ? overrideComboData : baseComboData;
            const itemsToRender = comboToRender.items || [];
            const comboPrice = comboToRender.combo_price || 0;
            const hasOverride = overrideActive;

            return (
                <div className="space-y-3">
                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-lg font-semibold text-purple-900 dark:text-purple-200">
                                {config.form_name} Combo
                            </h4>
                            <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                                ${comboPrice}
                            </div>
                        </div>

                        {itemsToRender.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {itemsToRender.map((item: any, index: number) => (
                                    <div
                                        key={index}
                                        className="p-3 bg-white dark:bg-purple-900/30 rounded-lg border border-purple-200 dark:border-purple-700"
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <h5 className="font-semibold text-purple-900 dark:text-purple-200">
                                                    {currentLanguage === 'ar' && item.ar && item.ar.item_name ? item.ar.item_name : item.item_name}
                                                </h5>
                                                {(item.note || (item.ar && item.ar.note)) && (
                                                    <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                                                        {currentLanguage === 'ar' && item.ar && item.ar.note ? item.ar.note : item.note}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="text-right ml-2">
                                                <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                                                    x{item.quantity}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-purple-600 dark:text-purple-400 italic">
                                {t('common.variants.noComboItemsConfigured', 'No combo items configured')}
                            </div>
                        )}

                        {hasOverride && (
                            <div className="mt-3 p-2 bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded text-xs text-amber-800 dark:text-amber-300">
                                {t('common.variants.comboCustomized', 'This combo has been customized for this item')}
                            </div>
                        )}
                    </div>

                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                        {isSelected ? (
                            <div className="flex flex-col sm:flex-row gap-2">
                                <button
                                    type="button"
                                    onClick={() => selectedVariant && openOverrideModal(config, selectedVariant)}
                                    disabled={!selectedVariant}
                                    className={`${baseActionClass} bg-amber-500 hover:bg-amber-600`}
                                >
                                    <FiSettings className="w-4 h-4" />
                                    {t('common.variants.customizeCombo', 'Customize Combo')}
                                    {hasOverride && (
                                        <span className="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded-full">
                                            {t('common.variants.overrideActive', 'Override active')}
                                        </span>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => removeVariant(selectedVariant.id)}
                                    className="w-full px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                                >
                                    <FiX className="w-4 h-4" />
                                    {t('common.variants.removeFromItem')}
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => addVariant(config.id)}
                                disabled={isSelected}
                                className={`w-full px-4 py-2 ${colorClasses.button} text-white rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                <FiPlus className="w-4 h-4" />
                                {isSelected ? t('common.variants.alreadyAdded', 'Already Added') : t('common.variants.addToItem')}
                            </button>
                        )}
                    </div>
                </div>
            );
        }

        // Handle legacy options array structure
        // For existing items with loaded variants, use the variant's data property
        // For new items, fall back to config.data
        const legacyVariantData = selectedVariant?.data;
        const baseOptions = Array.isArray(legacyVariantData) ? legacyVariantData : Array.isArray(config.data) ? config.data : [];

        // Override data source: items_variants.override_data (when item needs custom options)
        const overrideOptions = Array.isArray(selectedVariant?.override_data)
            ? (selectedVariant?.override_data as any[])
            : null;
        const overrideActive = selectedVariant?.override_data !== null && selectedVariant?.override_data !== undefined;

        // Render priority: override_data > variant.data > config.data
        const optionsToRender = overrideActive && Array.isArray(overrideOptions)
            ? overrideOptions
            : baseOptions;

        const hasOverride = overrideActive;

        if ((!optionsToRender || optionsToRender.length === 0)) {
            return (
                <div className="space-y-3">
                    <div className="text-sm text-gray-500 italic p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        {t('common.variants.noOptionsConfigured')}
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        {isSelected ? (
                            <button
                                type="button"
                                onClick={() => removeVariant(selectedVariant.id)}
                                className={removeActionClass}
                            >
                                <FiX className="w-4 h-4" />
                                {t('common.variants.removeFromItem')}
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => addVariant(config.id)}
                                disabled={isSelected}
                                className={`${baseActionClass} ${colorClasses.button}`}
                            >
                                <FiPlus className="w-4 h-4" />
                                {isSelected ? t('common.variants.alreadyAdded', 'Already Added') : t('common.variants.addToItem')}
                            </button>
                        )}
                    </div>
                </div>
            );
        }

        return (
            <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {optionsToRender.map((option: any, index: number) => {
                        const optionData = option as {
                            name?: string;
                            note?: string;
                            description?: string;
                            price?: number;
                            is_active?: boolean;
                            is_default?: boolean;
                        };
                        const isActive = optionData.is_active !== false;

                        return (
                            <div
                                key={index}
                                className={`p-4 border rounded-lg transition-all duration-200 ${isActive
                                    ? 'border-purple-300 bg-purple-50 dark:bg-purple-900/20'
                                    : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 opacity-50'
                                    }`}
                            >
                                <div className="flex items-start justify-between mb-2">
                                    <h5 className="font-semibold text-purple-900 dark:text-purple-200">
                                        {getLocalizedField(option, 'name') || `${t('common.variants.upgrade')} ${index + 1}`}
                                    </h5>
                                    {optionData.price !== undefined && optionData.price > 0 && (
                                        <span className="text-sm font-bold text-purple-600 dark:text-purple-400">
                                            +${optionData.price}
                                        </span>
                                    )}
                                </div>

                                {(optionData.note || (option.ar && option.ar.note)) && (
                                    <div className="text-xs text-purple-700 dark:text-purple-300 mb-2 bg-purple-100 dark:bg-purple-800/30 px-2 py-1 rounded">
                                        {getLocalizedField(option, 'note')}
                                    </div>
                                )}

                                {(optionData.description || (option.ar && option.ar.description)) && (
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                        {getLocalizedField(option, 'description')}
                                    </p>
                                )}

                                <div className="flex items-center gap-2 mt-2">
                                    {optionData.is_default && (
                                        <span className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 px-2 py-1 rounded-full">
                                            {t('common.variants.default')}
                                        </span>
                                    )}
                                    {!isActive && (
                                        <span className="text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 px-2 py-1 rounded-full">
                                            {t('common.variants.inactive')}
                                        </span>
                                    )}
                                    {hasOverride && (
                                        <span className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300 px-2 py-1 rounded-full">
                                            {t('common.variants.customized')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    {isSelected ? (
                        <div className="flex flex-col sm:flex-row gap-2">
                            <button
                                type="button"
                                onClick={() => selectedVariant && openOverrideModal(config, selectedVariant)}
                                disabled={!selectedVariant}
                                className="w-full px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <FiSettings className="w-4 h-4" />
                                {t('common.variants.customizeCombo', 'Customize Combo')}
                                {hasOverride && (
                                    <span className="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded-full">
                                        {t('common.variants.overrideActive', 'Override active')}
                                    </span>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => removeVariant(selectedVariant.id)}
                                className={removeActionClass}
                            >
                                <FiX className="w-4 h-4" />
                                {t('common.variants.removeFromItem')}
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => addVariant(config.id)}
                            disabled={isSelected}
                            className={`${baseActionClass} ${colorClasses.button}`}
                        >
                            <FiPlus className="w-4 h-4" />
                            {isSelected ? t('common.variants.alreadyAdded', 'Already Added') : t('common.variants.addToItem')}
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const overrideModal = (
        <ItemVariantOverrideModal
            isOpen={isOverrideModalOpen && !!activeOverrideConfig && !!activeVariant}
            onClose={closeOverrideModal}
            config={activeOverrideConfig}
            variant={activeVariant}
            onSave={handleSaveOverride}
            isSaving={isSavingOverride}
            externalError={overrideError}
        />
    );

    if (loading) {
        return (
            <>
                {overrideModal}
                <div className="space-y-4 overflow-y-auto">
                    <div className="animate-pulse space-y-4">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                        ))}
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            {overrideModal}
            <div ref={containerRef} className="min-h-[500px] overflow-y-auto overflow-x-hidden">
                {/* Header Section with Status */}
                <div className="sticky top-0 z-10 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-800 -mx-1 px-4 py-3 mb-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 shadow-lg shadow-orange-500/20">
                                <FiPackage className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                                    {t('common.variants.title', 'Item Variants')}
                                </h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {branchActions.length} {t('common.variants.available', 'available')}
                                </p>
                            </div>
                        </div>
                        {selectedVariants.length > 0 ? (
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full shadow-lg shadow-emerald-500/25">
                                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                <span className="text-xs font-semibold text-white">
                                    {selectedVariants.length} {t('common.variants.active', 'Active')}
                                </span>
                            </div>
                        ) : (
                            <span className="text-xs text-gray-400 dark:text-gray-500 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-full">
                                {t('common.variants.noneSelected', 'None selected')}
                            </span>
                        )}
                    </div>
                </div>

                {/* Active Variants Quick Access */}
                {selectedVariants.length > 0 && (
                    <div className="mx-1 mb-5 p-4 rounded-2xl bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-gray-800/50 dark:via-gray-800 dark:to-gray-800/50 border border-gray-200/50 dark:border-gray-700/50 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                                {t('common.variants.quickAccess', 'Quick Access')}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {selectedVariants.map((variant) => {
                                const config = branchActions.find(c => c.id === variant.branch_action_config_id);
                                if (!config) return null;
                                const hasOverride = variant.override_data !== null && variant.override_data !== undefined;
                                const hasVariantData = variant.data !== null && variant.data !== undefined;
                                const isExpanded = expandedSections.has(config.id);
                                const isSelection = config.action_type?.toLowerCase() === 'selection';
                                
                                return (
                                    <button
                                        key={variant.id}
                                        type="button"
                                        aria-pressed={isExpanded}
                                        onClick={() => { toggleSection(config.id); scrollToSection(config.id); }}
                                        className={`group relative flex items-center gap-2 px-3 py-2 rounded-xl border transition-all duration-300 ${
                                            isExpanded 
                                                ? 'bg-gray-900 dark:bg-white border-gray-900 dark:border-white shadow-lg scale-[1.02]' 
                                                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md'
                                        }`}
                                    >
                                        <div className={`p-1.5 rounded-lg transition-colors ${
                                            isSelection 
                                                ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400' 
                                                : 'bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400'
                                        } ${isExpanded ? 'bg-white/20 text-white dark:bg-gray-900/50 dark:text-gray-900' : ''}`}>
                                            {getVariantIcon(config.action_type)}
                                        </div>
                                        <span className={`text-sm font-medium transition-colors ${
                                            isExpanded 
                                                ? 'text-white dark:text-gray-900' 
                                                : 'text-gray-700 dark:text-gray-300'
                                        }`}>
                                            {currentLanguage === 'ar' && config.ar && typeof config.ar === 'object' && !Array.isArray(config.ar) && (config.ar as any).form_name
                                                ? (config.ar as any).form_name
                                                : config.form_name}
                                        </span>
                                        {hasVariantData && (
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-medium ${
                                                isExpanded 
                                                    ? 'bg-white/20 text-white dark:bg-gray-900/30 dark:text-gray-900' 
                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                            }`}>
                                                {Array.isArray(variant.data) ? `${variant.data.length}` : '✦'}
                                            </span>
                                        )}
                                        {hasOverride && (
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-medium ${
                                                isExpanded 
                                                    ? 'bg-amber-400/30 text-amber-100' 
                                                    : 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400'
                                            }`}>
                                                ★
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Empty State */}
                {branchActions.length === 0 ? (
                    <div className="relative mx-1 p-8 rounded-2xl bg-gradient-to-br from-gray-50 to-slate-100 dark:from-gray-800 dark:to-gray-900 border border-gray-200 dark:border-gray-700 overflow-hidden">
                        {/* Decorative elements */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-500/10 to-amber-500/10 rounded-full blur-3xl"></div>
                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-blue-500/10 to-purple-500/10 rounded-full blur-2xl"></div>
                        
                        <div className="relative text-center">
                            <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 mb-4">
                                <FiSettings className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                            </div>
                            <h4 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                {t('common.variants.noVariantsConfigured')}
                            </h4>
                            <p className="text-sm text-gray-500 dark:text-gray-500 max-w-sm mx-auto">
                                {t('common.variants.configureVariantsInSettings')}
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3 px-1 pb-2">
                        {branchActions.map((config, index) => {
                            const normalizedActionType = config.action_type?.toLowerCase() || '';
                            const isExpanded = expandedSections.has(config.id);
                            const colorClasses = getVariantColorClasses(config.action_type);
                            const selectedVariant = selectedVariants.find(v => v.branch_action_config_id === config.id);
                            const isSelected = !!selectedVariant;
                            const isSelectionType = normalizedActionType === 'selection';
                            const isUpgradeType = normalizedActionType === 'upgrade';

                            // Dynamic gradient based on type
                            const gradientClass = isSelectionType 
                                ? 'from-blue-500 to-cyan-500' 
                                : 'from-purple-500 to-pink-500';

                            return (
                                <div
                                    key={config.id}
                                    data-config-id={config.id}
                                    className={`group rounded-2xl overflow-hidden transition-all duration-300 ${
                                        isSelected
                                            ? 'bg-white dark:bg-gray-800 shadow-xl shadow-gray-200/50 dark:shadow-gray-900/50 ring-2 ring-offset-2 ring-offset-white dark:ring-offset-gray-900 ' + (isSelectionType ? 'ring-blue-500/50' : 'ring-purple-500/50')
                                            : 'bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/50 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-lg'
                                    }`}
                                    style={{ 
                                        animationDelay: `${index * 50}ms`,
                                    }}
                                >
                                    {/* Gradient accent bar for selected items */}
                                    {isSelected && (
                                        <div className={`h-1 bg-gradient-to-r ${gradientClass}`}></div>
                                    )}
                                    
                                    {/* Header */}
                                    <button
                                        type="button"
                                        onClick={() => toggleSection(config.id)}
                                        className="w-full flex items-center justify-between p-4 transition-colors hover:bg-gray-50/50 dark:hover:bg-gray-700/30"
                                    >
                                        <div className="flex items-center gap-4">
                                            {/* Icon with gradient background */}
                                            <div className="relative">
                                                <div className={`p-3 rounded-xl bg-gradient-to-br ${gradientClass} shadow-lg ${
                                                    isSelectionType ? 'shadow-blue-500/25' : 'shadow-purple-500/25'
                                                }`}>
                                                    {getVariantIcon(config.action_type)}
                                                    <div className="absolute inset-0 rounded-xl bg-white/20"></div>
                                                </div>
                                                {isSelected && (
                                                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white dark:border-gray-800 flex items-center justify-center">
                                                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* Text content */}
                                            <div className="text-left">
                                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                    <h4 className="font-semibold text-gray-900 dark:text-white">
                                                        {currentLanguage === 'ar' && config.ar && typeof config.ar === 'object' && !Array.isArray(config.ar) && (config.ar as any).form_name
                                                            ? (config.ar as any).form_name
                                                            : config.form_name}
                                                    </h4>
                                                    {isSelected && (
                                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 rounded-full">
                                                            <div className="w-1 h-1 bg-emerald-500 rounded-full animate-pulse"></div>
                                                            {t('common.variants.active')}
                                                        </span>
                                                    )}
                                                    {selectedVariant?.override_data !== null && selectedVariant?.override_data !== undefined && (
                                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 rounded-full">
                                                            <FiSettings className="w-2.5 h-2.5" />
                                                            {t('common.variants.customized', 'Custom')}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                                                    <span className={`px-2 py-0.5 rounded-md font-medium ${
                                                        isSelectionType 
                                                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' 
                                                            : 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                                                    }`}>
                                                        {config.action_type}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <span className={`w-1.5 h-1.5 rounded-full ${config.is_required ? 'bg-red-500' : 'bg-gray-400'}`}></span>
                                                        {config.is_required ? t('common.variants.required') : t('common.variants.optional')}
                                                    </span>
                                                    <span className="text-gray-400">
                                                        {config.min_selection}-{config.max_selection} {t('common.variants.selections')}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Expand/Collapse indicator */}
                                        <div className={`p-2 rounded-lg transition-all duration-300 ${
                                            isExpanded 
                                                ? 'bg-gray-100 dark:bg-gray-700 rotate-180' 
                                                : 'bg-transparent group-hover:bg-gray-100 dark:group-hover:bg-gray-700'
                                        }`}>
                                            <FiChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                                        </div>
                                    </button>

                                    {/* Content with smooth animation */}
                                    {isExpanded && (
                                        <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/50">
                                            <div className="pt-4">
                                                {isSelectionType && renderSelectionVariant(config, colorClasses)}
                                                {isUpgradeType && renderUpgradeVariant(config, colorClasses)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </>
    );
};

export default DynamicOptions;
